import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import PostBox1 from '../helpers/PostBox1';
import ReportModal from './ReportModal';

const API = 'https://buzzinguniverse.com/backend/api';

/**
 * "⋯" menu for a profile or a group/company page (Facebook style).
 *  - targetType: 'user' | 'group' | 'company'
 *  - targetId / targetName: who the page belongs to
 *  - canReport: hide Report on your own profile/page
 *  - extraItems: [{ label, icon, onClick, danger }] e.g. Block / Unblock on profiles
 */
const PageActionsMenu = ({ targetType, targetId, targetName, canReport = true, extraItems = [] }) => {
    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));
    const [open, setOpen] = useState(false);
    const [modal, setModal] = useState(null); // 'search' | 'report' | null
    const wrapRef = useRef(null);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const pick = (fn) => (e) => {
        setOpen(false);
        fn(e);
    };

    return (
        <div className="page-actions" ref={wrapRef}>
            <button type="button" className="page-actions-trigger" title="More options" onClick={() => setOpen((v) => !v)}>
                <i className="fas fa-ellipsis-h" />
            </button>

            {open && (
                <ul className="page-actions-dropdown">
                    <li onClick={pick(() => setModal('search'))}>
                        <i className="fas fa-search" /> Search
                    </li>
                    {canReport && auth?.id && (
                        <li onClick={pick(() => setModal('report'))}>
                            <i className="fas fa-flag" /> Report {targetType === 'user' ? 'Profile' : targetType === 'company' ? 'Company' : 'Group'}
                        </li>
                    )}
                    {extraItems.map((item) => (
                        <li key={item.label} className={item.danger ? 'danger' : ''} onClick={pick(item.onClick)}>
                            <i className={item.icon} /> {item.label}
                        </li>
                    ))}
                </ul>
            )}

            {modal === 'search' && createPortal(
                <SearchModal targetType={targetType} targetId={targetId} targetName={targetName} auth={auth} onClose={() => setModal(null)} />,
                document.body
            )}
            {modal === 'report' && createPortal(
                <ReportModal targetType={targetType} targetId={targetId} targetName={targetName} auth={auth} onClose={() => setModal(null)} />,
                document.body
            )}
        </div>
    );
};

const SearchModal = ({ targetType, targetId, targetName, auth, onClose }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState('');

    const runSearch = async (q, pageNum = 1) => {
        if (!q.trim()) return;
        setLoading(true);
        try {
            const params = { search: q.trim(), visitor_id: auth?.id || 0, page: pageNum };
            if (targetType === 'user') params.userid = targetId;
            else params.group_id = targetId;
            const res = await axios.get(`${API}/get-post-request`, { params });
            const rows = res.data.code === 200 ? (res.data.data || []).filter(Boolean) : [];
            setResults((prev) => (pageNum === 1 ? rows : [...prev, ...rows]));
            setTotal(res.data.code === 200 ? res.data.total || 0 : 0);
            setPage(pageNum);
            setSearched(q.trim());
        } catch (err) {
            console.error(err);
            if (pageNum === 1) setResults([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-actions-backdrop" onClick={onClose}>
            <div className="page-actions-modal page-actions-modal-wide" onClick={(e) => e.stopPropagation()}>
                <div className="page-actions-modal-head">
                    <h5>Search {targetName}'s posts</h5>
                    <span className="page-actions-close" onClick={onClose}>&times;</span>
                </div>
                <form className="page-actions-search" onSubmit={(e) => { e.preventDefault(); runSearch(query, 1); }}>
                    <i className="fas fa-search" />
                    <input autoFocus type="text" placeholder="Search posts..." value={query} onChange={(e) => setQuery(e.target.value)} />
                    <button type="submit" disabled={!query.trim() || loading}>Search</button>
                </form>
                <div className="page-actions-results personal_des_box_main">
                    {results.map((post, i) => (
                        <PostBox1 key={post.post_data?.id || i} post={post} usrAuth={auth} setSinlgePost={() => {}} inGroup={false} groupAdmins={[]} />
                    ))}
                    {loading && <div className="page-actions-empty"><div className="spinner-border text-primary" role="status" /></div>}
                    {!loading && searched && results.length === 0 && (
                        <div className="page-actions-empty">No posts found for "<strong>{searched}</strong>"</div>
                    )}
                    {!loading && results.length > 0 && results.length < total && (
                        <div className="page-actions-empty">
                            <button type="button" className="btn btn-outline-primary" onClick={() => runSearch(searched, page + 1)}>Load more</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PageActionsMenu;
